// External libraries

// Internal imports
import { useState, useEffect, useCallback, useRef } from 'react';

export interface NetworkStatus {
  online: boolean;
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  downlink: number;
  rtt: number;
  saveData: boolean;
  since: Date | null;
  offlineSince: Date | null;
  wasOffline: boolean;
}

export interface NetworkOptions {
  checkInterval?: number;
  pingUrl?: string;
  timeout?: number;
  retries?: number;
}

const DEFAULT_OPTIONS: Required<NetworkOptions> = {
  checkInterval: 30000, // 30 seconds
  pingUrl: '/api/health',
  timeout: 5000,
  retries: 3
};

export function useNetworkAware(options: NetworkOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    online: navigator.onLine,
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false,
    since: null,
    offlineSince: navigator.onLine ? null : new Date(),
    wasOffline: false
  });

  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'excellent' | 'good' | 'fair' | 'poor'>('good');

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const lastOnlineCheckRef = useRef<Date>(new Date());

  const getConnectionInfo = useCallback(() => {
    if (!('connection' in navigator)) {
      return {
        effectiveType: 'unknown' as const,
        downlink: 0,
        rtt: 0,
        saveData: false
      };
    }

    const connection = (navigator as any).connection;
    return {
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink || 0,
      rtt: connection.rtt || 0,
      saveData: connection.saveData || false
    };
  }, []);

  const assessConnectionQuality = useCallback((info: Partial<NetworkStatus>) => {
    const { effectiveType, downlink, rtt } = info;

    if (effectiveType === '4g' && downlink > 10 && rtt < 100) {
      return 'excellent';
    } else if (effectiveType === '4g' || (effectiveType === '3g' && downlink > 2 && rtt < 300)) {
      return 'good';
    } else if (effectiveType === '3g' || (effectiveType === '2g' && downlink > 0.1)) {
      return 'fair';
    } else {
      return 'poor';
    }
  }, []);

  const checkActualConnectivity = useCallback(async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), opts.timeout);

      const response = await fetch(opts.pingUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Network check timeout');
      } else {
        console.log('Network check failed:', error);
      }
      return false;
    }
  }, [opts]);

  const updateNetworkStatus = useCallback(async (forceCheck = false) => {
    const isOnline = navigator.onLine;
    const connectionInfo = getConnectionInfo();
    const currentTime = new Date();

    let actualOnline = isOnline;

    // Force actual connectivity check when requested or on recovery
    if (forceCheck || (networkStatus.wasOffline && isOnline)) {
      actualOnline = await checkActualConnectivity();
    }

    const wasOffline = networkStatus.offlineSince !== null;
    const newStatus: NetworkStatus = {
      online: actualOnline,
      ...connectionInfo,
      since: actualOnline && !networkStatus.online ? currentTime : networkStatus.since,
      offlineSince: !actualOnline && networkStatus.online ? currentTime : networkStatus.offlineSince,
      wasOffline
    };

    const isSlow = connectionInfo.effectiveType === 'slow-2g' ||
                   connectionInfo.effectiveType === '2g' ||
                   connectionInfo.saveData;

    const quality = assessConnectionQuality(newStatus);

    setNetworkStatus(newStatus);
    setIsSlowConnection(isSlow);
    setConnectionQuality(quality);
    lastOnlineCheckRef.current = currentTime;

    // Reset retry count on successful check
    if (actualOnline) {
      retryCountRef.current = 0;
    }

    return newStatus;
  }, [networkStatus, getConnectionInfo, checkActualConnectivity, assessConnectionQuality]);

  const scheduleNextCheck = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    checkIntervalRef.current = setInterval(() => {
      updateNetworkStatus(true);
    }, opts.checkInterval);
  }, [updateNetworkStatus, opts.checkInterval]);

  const handleOnline = useCallback(() => {
    console.log('Network connection restored');
    updateNetworkStatus(true);
  }, [updateNetworkStatus]);

  const handleOffline = useCallback(() => {
    console.log('Network connection lost');
    updateNetworkStatus(false);
  }, [updateNetworkStatus]);

  const handleConnectionChange = useCallback(() => {
    console.log('Network connection properties changed');
    updateNetworkStatus();
  }, [updateNetworkStatus]);

  // Execute actions with network awareness
  const executeWithNetworkFallback = useCallback(async <T>(
    onlineAction: () => Promise<T>,
    offlineAction: () => Promise<T> | T,
    options?: { timeout?: number; retries?: number }
  ): Promise<T> => {
    const timeout = options?.timeout || opts.timeout;
    const maxRetries = options?.retries || opts.retries;

    if (networkStatus.online && !isSlowConnection) {
      try {
        return await Promise.race([
          onlineAction(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Network timeout')), timeout)
          )
        ]);
      } catch (error) {
        console.warn('Online action failed, falling back:', error);
      }
    }

    // Fallback to offline action
    return offlineAction();
  }, [networkStatus.online, isSlowConnection, opts.timeout, opts.retries]);

  // Queue actions for when network is available
  const queueAction = useCallback(<T>(action: () => Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
      if (networkStatus.online) {
        action().then(resolve).catch(reject);
      } else {
        const interval = setInterval(() => {
          if (networkStatus.online) {
            clearInterval(interval);
            action().then(resolve).catch(reject);
          }
        }, 1000);
      }
    });
  }, [networkStatus.online]);

  // Batch network requests
  const batchRequests = useCallback(async <T>(
    requests: Array<() => Promise<T>>,
    options?: { concurrency?: number; delay?: number }
  ): Promise<T[]> => {
    const concurrency = options?.concurrency || 3;
    const delay = options?.delay || 100;

    if (isSlowConnection) {
      // Sequential execution for slow connections
      const results: T[] = [];
      for (const request of requests) {
        const result = await request();
        results.push(result);
        if (delay > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      return results;
    } else {
      // Concurrent execution for fast connections
      const chunks = [];
      for (let i = 0; i < requests.length; i += concurrency) {
        chunks.push(requests.slice(i, i + concurrency));
      }

      const results: T[] = [];
      for (const chunk of chunks) {
        const chunkResults = await Promise.all(chunk.map(req => req()));
        results.push(...chunkResults);
      }
      return results;
    }
  }, [isSlowConnection]);

  // Adaptive resource loading
  const adaptiveLoad = useCallback(async <T>(
    resources: Array<{
      highQuality: () => Promise<T>;
      lowQuality: () => Promise<T>;
      priority?: 'high' | 'medium' | 'low';
    }>
  ): Promise<T[]> => {
    const loadResource = async (resource: typeof resources[0]) => {
      if (isSlowConnection || connectionQuality === 'poor') {
        return resource.lowQuality();
      } else if (connectionQuality === 'fair' && resource.priority !== 'high') {
        return resource.lowQuality();
      } else {
        return resource.highQuality();
      }
    };

    return Promise.all(resources.map(loadResource));
  }, [isSlowConnection, connectionQuality]);

  useEffect(() => {
    // Initial check
    updateNetworkStatus();

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', handleConnectionChange);
    }

    // Schedule periodic checks
    scheduleNextCheck();

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        connection.removeEventListener('change', handleConnectionChange);
      }

      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [updateNetworkStatus, handleOnline, handleOffline, handleConnectionChange, scheduleNextCheck]);

  return {
    networkStatus,
    isSlowConnection,
    connectionQuality,
    executeWithNetworkFallback,
    queueAction,
    batchRequests,
    adaptiveLoad,
    checkConnection: () => updateNetworkStatus(true),
    isOnline: networkStatus.online,
    wasOffline: networkStatus.wasOffline,
    offlineDuration: networkStatus.offlineSince
      ? Date.now() - networkStatus.offlineSince.getTime()
      : 0
  };
}

// Hook for managing offline storage and sync
export function useOfflineStorage<T extends { id: string; timestamp?: number }>(
  storageKey: string,
  networkAware: ReturnType<typeof useNetworkAware>
) {
  const [offlineData, setOfflineData] = useState<T[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Load offline data on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setOfflineData(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load offline data:', error);
    }
  }, [storageKey]);

  // Save data to offline storage
  const saveOffline = useCallback((data: T[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
      setOfflineData(data);
    } catch (error) {
      console.error('Failed to save offline data:', error);
      // Handle storage quota exceeded
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        // Implement cleanup strategy
        const sortedData = [...data].sort((a, b) =>
          (b.timestamp || 0) - (a.timestamp || 0)
        );
        const trimmedData = sortedData.slice(0, Math.floor(sortedData.length * 0.8));
        localStorage.setItem(storageKey, JSON.stringify(trimmedData));
        setOfflineData(trimmedData);
      }
    }
  }, [storageKey]);

  // Add item to offline storage
  const addOfflineItem = useCallback((item: T) => {
    const itemWithTimestamp = {
      ...item,
      timestamp: Date.now()
    };
    saveOffline([...offlineData, itemWithTimestamp]);
  }, [offlineData, saveOffline]);

  // Remove item from offline storage
  const removeOfflineItem = useCallback((id: string) => {
    const filtered = offlineData.filter(item => item.id !== id);
    saveOffline(filtered);
  }, [offlineData, saveOffline]);

  // Sync offline data with server
  const syncWithServer = useCallback(async (
    syncAction: (items: T[]) => Promise<void>
  ) => {
    if (!networkAware.isOnline || offlineData.length === 0 || isSyncing) {
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      await syncAction(offlineData);
      // Clear successfully synced data
      saveOffline([]);
      console.log(`Successfully synced ${offlineData.length} items`);
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Sync failed');
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [networkAware.isOnline, offlineData, isSyncing, saveOffline]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (networkAware.wasOffline && networkAware.isOnline && offlineData.length > 0) {
      // Delay sync slightly to ensure stable connection
      const timer = setTimeout(() => {
        console.log('Auto-syncing offline data...');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [networkAware.wasOffline, networkAware.isOnline, offlineData.length]);

  return {
    offlineData,
    isSyncing,
    syncError,
    saveOffline,
    addOfflineItem,
    removeOfflineItem,
    syncWithServer,
    hasOfflineData: offlineData.length > 0,
    offlineCount: offlineData.length
  };
}